import {usePathname} from 'next/navigation';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useOnScreenById} from '@utils/hooks/useOnScreen';
import {useWindowFocusListener} from '@utils/hooks/useWindowFocusListener';

import {AdCallbacksFactory, AdRefreshTrigger, AdsApiAdapter} from '../types';

type Props = {
  refresh: AdsApiAdapter['refresh'];
  containerId: string;
  isFloorAd?: boolean; // как родной флур так и баннер который притворяется флуром
  enabled?: boolean;
  config?: {
    intervalMs: number;
    triggers: AdRefreshTrigger[];
    logging?: boolean | undefined;
  };
};

export const useAutorefresh = ({
  refresh,
  containerId,
  isFloorAd = false,
  enabled: externalEnabled = true,
  config,
}: Props): {
  createAutorefreshCallbacks: AdCallbacksFactory;
  getRefreshNumber: () => number;
} => {
  const autorefreshEnabled =
    externalEnabled && !!config && config.triggers.length > 0;
  const refreshRequestCountRef = useRef(0);

  const requestRefresh = useCallback(
    (trigger: AdRefreshTrigger) => {
      refresh(trigger, refreshRequestCountRef.current);
      refreshRequestCountRef.current++;
    },
    [refresh],
  );

  const log = useCallback(
    (message: string) => {
      if (config?.logging) {
        // eslint-disable-next-line no-console
        console.log(`Ad autorefresh ${containerId}: ${message}`);
      }
    },
    [config, containerId],
  );

  const [isAdLive, setIsAdLive] = useState(false);

  const isOnScreen = useOnScreenById({
    elementId: containerId,
    initialState: false,
    enabled: autorefreshEnabled && !isFloorAd,
  });
  const isVisible = isFloorAd || isOnScreen; // флур и так приколочен

  const isVisibleAndLiveRef = useRef(false);
  useEffect(() => {
    isVisibleAndLiveRef.current = isVisible && isAdLive;
    log(`change: isVisible=${isVisible}, isAdLive=${isAdLive}`);
  }, [isVisible, isAdLive, log]);

  // рефреш на навигацию
  // только для floor_ad(-лайк), все остальные при навигации все равно пересоздаются
  const shouldRefreshByPathnameChange =
    autorefreshEnabled && isFloorAd && config.triggers.includes('navigation');

  const pathname = usePathname();
  useEffect(() => {
    if (!shouldRefreshByPathnameChange) {
      return;
    }
    if (isAdLive) {
      log('requesting on pathname change');
      requestRefresh('navigation');
    } else {
      log('skipping on pathname change, ad is not live');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // рефреш на фокус окна
  const shouldRefreshOnWindowFocus =
    autorefreshEnabled && config.triggers.includes('window_focus');
  const isWindowFocused = useWindowFocusListener(
    useCallback(() => {
      if (!shouldRefreshOnWindowFocus) {
        return;
      }
      if (isVisibleAndLiveRef.current) {
        log('requesting on window focus');
        requestRefresh('window_focus');
      } else {
        log('skipping on window focus, not visible or not live');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRefreshOnWindowFocus]),
  );

  // рефреш по таймеру
  const shouldRefreshOnTimer =
    autorefreshEnabled &&
    isAdLive &&
    isWindowFocused &&
    config.triggers.includes('timer') &&
    config.intervalMs > 0;

  useEffect(() => {
    if (!shouldRefreshOnTimer) {
      return;
    }
    log('scheduling timer');
    const intervalHandle = setInterval(() => {
      log('requesting on timer');
      requestRefresh('timer');
    }, config.intervalMs);
    return () => {
      log('cleaning up timer');
      clearInterval(intervalHandle);
    };
  }, [shouldRefreshOnTimer, config, log, requestRefresh]);

  return useMemo(
    () => ({
      createAutorefreshCallbacks: () => ({
        onRender: () => setIsAdLive(true),
        onClose: () => setIsAdLive(false),
      }),
      getRefreshNumber: () => refreshRequestCountRef.current,
    }),
    [],
  );
};
